import { useState, useEffect } from 'react';
import { X, Cookie, Settings, Check, Shield } from 'lucide-react';

/**
 * CookieBanner - GDPR Compliant Cookie Consent Banner
 * Appears on first visit, allows Accept All, Reject All, or Configure
 */
export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);
    // Notify hooks (GA4, Meta Pixel, etc.) about consent change
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
  };

  const rejectAll = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      {showBanner && !showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom duration-500">
          <div className="max-w-4xl mx-auto backdrop-blur-xl bg-[#121212]/95 border border-white/10 shadow-2xl">
            {/* Gold top line */}
            <div className="h-1 bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent" />
            
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="hidden md:flex w-12 h-12 rounded-full bg-[var(--primary)]/10 items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[var(--primary)]" />
                </div>
                
                <div className="flex-grow">
                  <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">
                    Utilizamos cookies
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    En EventCinema.es utilizamos cookies propias y de terceros para mejorar tu experiencia, 
                    analizar el tráfico y mostrarte contenido personalizado. Puedes aceptar todas, rechazarlas 
                    o configurar tus preferencias.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={acceptAll}
                      className="flex-1 bg-[var(--primary)] hover:bg-[#F2D06B] text-black font-bold py-3 px-6 uppercase tracking-widest text-xs transition-all duration-300"
                    >
                      Aceptar todas
                    </button>
                    <button
                      onClick={rejectAll}
                      className="flex-1 border border-white/20 hover:border-white/40 text-white font-medium py-3 px-6 uppercase tracking-widest text-xs transition-all duration-300"
                    >
                      Rechazar todas
                    </button>
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="flex-1 border border-[var(--primary)]/40 hover:border-[var(--primary)] text-[var(--primary)] font-medium py-3 px-6 uppercase tracking-widest text-xs transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <Settings size={14} />
                      Configurar
                    </button>
                  </div>
                  
                  <p className="text-white/40 text-xs mt-4">
                    Más información en nuestra{' '}
                    <a href="/politica-cookies" className="text-[var(--primary)] hover:underline">
                      Política de Cookies
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-in fade-in duration-300">
          <div className="w-full max-w-lg backdrop-blur-xl bg-[#121212] border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-[var(--primary)]" />
                <h3 className="font-['Playfair_Display'] text-xl text-white">
                  Configurar Cookies
                </h3>
              </div>
              <button 
                onClick={() => setShowPreferences(false)}
                className="text-white/50 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Cookie Categories */}
            <div className="p-6 space-y-6">
              {/* Necessary */}
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">Cookies Técnicas (Necesarias)</h4>
                  <span className="text-xs text-[var(--primary)] bg-[var(--primary)]/10 px-2 py-1 rounded">
                    Siempre activas
                  </span>
                </div>
                <p className="text-white/50 text-sm">
                  Imprescindibles para el funcionamiento del sitio. Permiten la navegación, 
                  gestión del carrito y acceso a zonas seguras.
                </p>
              </div>

              {/* Analytics */}
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">Cookies Analíticas</h4>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      preferences.analytics ? 'bg-[var(--primary)]' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      preferences.analytics ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                <p className="text-white/50 text-sm">
                  Nos permiten contar visitas y analizar el comportamiento para mejorar la plataforma. 
                  Usamos Google Analytics.
                </p>
              </div>

              {/* Marketing */}
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">Cookies de Marketing</h4>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      preferences.marketing ? 'bg-[var(--primary)]' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      preferences.marketing ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                <p className="text-white/50 text-sm">
                  Permiten mostrar anuncios relevantes basados en tus intereses. 
                  Usamos Meta (Facebook) para campañas publicitarias.
                </p>
              </div>

              {/* Preferences */}
              <div className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">Cookies de Preferencias</h4>
                  <button
                    onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      preferences.preferences ? 'bg-[var(--primary)]' : 'bg-white/20'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      preferences.preferences ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
                <p className="text-white/50 text-sm">
                  Recuerdan tu idioma, ciudad preferida y otras configuraciones personalizadas 
                  para mejorar tu experiencia.
                </p>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-[#121212] border-t border-white/10 p-6 flex gap-3">
              <button
                onClick={rejectAll}
                className="flex-1 border border-white/20 hover:border-white/40 text-white font-medium py-3 px-4 uppercase tracking-widest text-xs transition-all duration-300"
              >
                Rechazar todas
              </button>
              <button
                onClick={saveCustom}
                className="flex-1 bg-[var(--primary)] hover:bg-[#F2D06B] text-black font-bold py-3 px-4 uppercase tracking-widest text-xs transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Check size={14} />
                Guardar preferencias
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
